import { Button } from "@shared/ui/button";
import {
  ArrowLeft,
  Banknote,
  Calendar,
  Clock,
  Download,
  Home,
  Hash,
  User,
  LogOut,
} from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DrillDownDashboard } from "../dashboard/dashboard";
import { PageHeader } from "@shared/components/PageHeader";
import { PAGE_SIZE } from "@shared/utils/constants";

import { DrillDownList } from "../list/list";
import {
  useReconciliationById,
  useReconciliationDrillDown,
} from "../../../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

const DetailLandingPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { channelName } = useParams();
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || PAGE_SIZE);
  const search = searchParams.get("search") || "";
  const { data: reconciliationDetails } = useReconciliationById(
    id,
    remoteUtilityId,
  );

  const { data: drillDownData, isLoading } = useReconciliationDrillDown(
    id || "",
    channelName || "",
    currentPage,
    limit,
    search,
  );

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page.toString());
    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (searchTerm.trim()) {
      newParams.set("search", searchTerm.trim());
    } else {
      newParams.delete("search");
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const status = reconciliationDetails?.statusDisplay;

  const getStatusColors = (statusVal: string) => {
    if (!statusVal) return { line: "bg-gray-600", badge: "bg-gray-100 text-gray-800" };
    const lower = statusVal.toLowerCase();
    if (lower === "not started") return { line: "bg-red-600", badge: "bg-red-100 text-red-800" };
    if (lower === "in progress") return { line: "bg-amber-600", badge: "bg-amber-100 text-amber-800" };
    if (lower === "signed off" || lower === "matched") return { line: "bg-green-600", badge: "bg-green-100 text-green-800" };
    return { line: "bg-gray-600", badge: "bg-gray-100 text-gray-800" };
  };

  const statusColors = getStatusColors(status);

  return (
    <div className="flex flex-col gap-6 p-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() =>
              navigate(`/cx/payments/day-end-reconcilation/view/${id}`)
            }
          />

          {/* Page Header */}
          <div className="flex flex-col">
            <PageHeader
              title={`${drillDownData?.paymentMode} — Channel Drill-Down`}
              subtitle={`View detailed transaction breakdown for the ${drillDownData?.paymentMode} channel in reconciliation`}
            />
          </div>
        </div>
      </div>

      {/* File Info Card */}
      <div className="bg-white border rounded-xl overflow-hidden shadow-sm relative">
        <div
          className={`absolute left-0 top-0 bottom-0 w-1.5 ${statusColors.line}`}
        ></div>
        <div className="p-5 pl-7">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-bold text-gray-900">
                {reconciliationDetails?.periodFrom} -{" "}
                {reconciliationDetails?.periodTo}
              </h1>
              <span
                className={`px-3 py-1 text-xs font-semibold rounded-full ${statusColors.badge}`}
              >
                {status}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500 font-medium">
              <div className="flex items-center gap-1.5">
                <Hash className="h-4 w-4" />
                {reconciliationDetails?.runId}
              </div>
              <div className="flex items-center gap-1.5">
                <User className="h-4 w-4" />
                {reconciliationDetails?.signedOffBy || "N/A"}
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {reconciliationDetails?.createdDate}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="mt-2 flex flex-col gap-6">
        <DrillDownDashboard data={drillDownData?.kpis} />
        <DrillDownList
          drillDownData={drillDownData}
          isLoading={isLoading}
          currentPage={currentPage}
          limit={limit}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
        />
      </div>
    </div>
  );
};

export default DetailLandingPage;
